import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RefreshCwIcon, SearchIcon, FilterIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, TrendingUpIcon, TrendingDownIcon, DollarSignIcon, FileTextIcon } from 'lucide-react';
import { Refund, RefundStatus, RefundReason } from '../../../shared/types/Refund';
export function Refunds() {
  const {
    t
  } = useTranslation('refunds');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<RefundStatus | 'all'>('all');
  const [reasonFilter, setReasonFilter] = useState<RefundReason | 'all'>('all');
  // Mock data
  const refunds: Refund[] = [{
    id: 'RF-2024-0045',
    refundId: 'RF-2024-0045',
    transactionId: 'TXN-2026-5512',
    originalAmount: 45000,
    refundAmount: 45000,
    currency: 'MVR',
    status: 'pending_approval',
    reason: 'customer_request',
    reasonDescription: 'Customer requested refund — order delivered late',
    requestedBy: 'Ahmad Rahman',
    requestedAt: '2026-05-04T09:00:00Z',
    customerName: 'Ahmad Rahman',
    customerEmail: 'ahmad.rahman@email.com',
    paymentMethod: 'Visa',
    cardLast4: '4521',
    merchantName: 'Bank of Maldives',
    merchantId: 'MERCH-MY-001',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2026-05-04T09:00:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Ahmad Rahman'
    }, {
      id: '2',
      timestamp: '2026-05-04T09:05:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }],
    isPartial: false
  }, {
    id: '1',
    refundId: 'REF-2024-001',
    transactionId: 'TXN-2024-1234',
    originalAmount: 125000,
    refundAmount: 125000,
    currency: 'MVR',
    status: 'pending_approval',
    reason: 'customer_request',
    reasonDescription: 'Customer requested cancellation due to order error',
    requestedBy: 'John Smith',
    requestedAt: '2024-01-18T10:30:00Z',
    customerName: 'John Smith',
    customerEmail: 'john.smith@email.com',
    paymentMethod: 'Visa',
    cardLast4: '4242',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-18T10:30:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'John Smith'
    }, {
      id: '2',
      timestamp: '2024-01-18T10:35:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }],
    isPartial: false
  }, {
    id: '2',
    refundId: 'REF-2024-002',
    transactionId: 'TXN-2024-1567',
    originalAmount: 87500,
    refundAmount: 43750,
    currency: 'MVR',
    status: 'processing',
    reason: 'product_defective',
    reasonDescription: 'Product arrived damaged, partial refund issued',
    requestedBy: 'Sarah Johnson',
    requestedAt: '2024-01-15T14:20:00Z',
    approvedBy: 'Mike Manager',
    approvedAt: '2024-01-15T15:00:00Z',
    customerName: 'Sarah Johnson',
    customerEmail: 'sarah.j@email.com',
    paymentMethod: 'Mastercard',
    cardLast4: '5555',
    merchantName: 'XYZ Shop',
    merchantId: 'MERCH-002',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Mike Manager',
      approverEmail: 'mike.m@merchant.com',
      status: 'approved',
      timestamp: '2024-01-15T15:00:00Z',
      notes: 'Approved partial refund for damaged item'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T14:20:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Sarah Johnson'
    }, {
      id: '2',
      timestamp: '2024-01-15T14:25:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }, {
      id: '3',
      timestamp: '2024-01-15T15:00:00Z',
      status: 'approved',
      description: 'Refund approved by Mike Manager',
      actor: 'Mike Manager'
    }, {
      id: '4',
      timestamp: '2024-01-15T15:05:00Z',
      status: 'processing',
      description: 'Processing refund to original payment method'
    }],
    isPartial: true
  }, {
    id: '3',
    refundId: 'REF-2024-003',
    transactionId: 'TXN-2024-0892',
    originalAmount: 45000,
    refundAmount: 45000,
    currency: 'MVR',
    status: 'completed',
    reason: 'duplicate_charge',
    reasonDescription: 'Duplicate transaction, full refund issued',
    requestedBy: 'Michael Brown',
    requestedAt: '2024-01-10T09:00:00Z',
    approvedBy: 'Admin User',
    approvedAt: '2024-01-10T09:15:00Z',
    processedAt: '2024-01-10T09:20:00Z',
    completedAt: '2024-01-12T10:30:00Z',
    customerName: 'Michael Brown',
    customerEmail: 'mbrown@email.com',
    paymentMethod: 'Visa',
    cardLast4: '1234',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Admin User',
      approverEmail: 'admin@merchant.com',
      status: 'approved',
      timestamp: '2024-01-10T09:15:00Z',
      notes: 'Duplicate charge confirmed, approved immediately'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-10T09:00:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Michael Brown'
    }, {
      id: '2',
      timestamp: '2024-01-10T09:15:00Z',
      status: 'approved',
      description: 'Refund approved by Admin User',
      actor: 'Admin User'
    }, {
      id: '3',
      timestamp: '2024-01-10T09:20:00Z',
      status: 'processing',
      description: 'Processing refund to original payment method'
    }, {
      id: '4',
      timestamp: '2024-01-12T10:30:00Z',
      status: 'completed',
      description: 'Refund successfully completed'
    }],
    isPartial: false
  }, {
    id: '4',
    refundId: 'REF-2024-004',
    transactionId: 'TXN-2024-0654',
    originalAmount: 165000,
    refundAmount: 165000,
    currency: 'MVR',
    status: 'rejected',
    reason: 'customer_request',
    reasonDescription: 'Customer requested refund after 30-day window',
    requestedBy: 'Emily Davis',
    requestedAt: '2024-01-08T11:20:00Z',
    rejectedBy: 'Sarah Manager',
    rejectedAt: '2024-01-08T14:00:00Z',
    rejectionReason: 'Refund request exceeds the 30-day return policy window',
    customerName: 'Emily Davis',
    customerEmail: 'emily.d@email.com',
    paymentMethod: 'Mastercard',
    cardLast4: '8888',
    merchantName: 'XYZ Shop',
    merchantId: 'MERCH-002',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'rejected',
      timestamp: '2024-01-08T14:00:00Z',
      notes: 'Request outside return policy window'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-08T11:20:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Emily Davis'
    }, {
      id: '2',
      timestamp: '2024-01-08T11:25:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }, {
      id: '3',
      timestamp: '2024-01-08T14:00:00Z',
      status: 'rejected',
      description: 'Refund rejected by Sarah Manager',
      actor: 'Sarah Manager'
    }],
    isPartial: false
  }, {
    id: '5',
    refundId: 'REF-2024-005',
    transactionId: 'TXN-2024-2341',
    originalAmount: 95000,
    refundAmount: 95000,
    currency: 'MVR',
    status: 'requested',
    reason: 'product_not_received',
    reasonDescription: 'Customer claims product was never delivered',
    requestedBy: 'David Wilson',
    requestedAt: '2024-01-19T15:45:00Z',
    customerName: 'David Wilson',
    customerEmail: 'dwilson@email.com',
    paymentMethod: 'Visa',
    cardLast4: '6789',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-19T15:45:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'David Wilson'
    }],
    isPartial: false
  }, {
    id: '6',
    refundId: 'REF-2024-006',
    transactionId: 'TXN-2024-1823',
    originalAmount: 215000,
    refundAmount: 215000,
    currency: 'MVR',
    status: 'failed',
    reason: 'billing_error',
    reasonDescription: 'Billing error detected, refund attempted',
    requestedBy: 'System',
    requestedAt: '2024-01-17T08:00:00Z',
    approvedBy: 'Admin User',
    approvedAt: '2024-01-17T08:05:00Z',
    processedAt: '2024-01-17T08:10:00Z',
    customerName: 'Jennifer Lee',
    customerEmail: 'jlee@email.com',
    paymentMethod: 'Visa',
    cardLast4: '9876',
    merchantName: 'XYZ Shop',
    merchantId: 'MERCH-002',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Admin User',
      approverEmail: 'admin@merchant.com',
      status: 'approved',
      timestamp: '2024-01-17T08:05:00Z',
      notes: 'System detected billing error, auto-approved'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-17T08:00:00Z',
      status: 'requested',
      description: 'Refund requested by system',
      actor: 'System'
    }, {
      id: '2',
      timestamp: '2024-01-17T08:05:00Z',
      status: 'approved',
      description: 'Refund auto-approved',
      actor: 'Admin User'
    }, {
      id: '3',
      timestamp: '2024-01-17T08:10:00Z',
      status: 'processing',
      description: 'Processing refund to original payment method'
    }, {
      id: '4',
      timestamp: '2024-01-17T08:15:00Z',
      status: 'failed',
      description: 'Refund failed - card expired'
    }],
    isPartial: false
  }];
  // Calculate statistics
  const totalRefunds = refunds.length;
  const pendingRefunds = refunds.filter(r => ['requested', 'pending_approval', 'approved', 'processing'].includes(r.status)).length;
  const completedRefunds = refunds.filter(r => r.status === 'completed').length;
  const totalRefundAmount = refunds.filter(r => r.status === 'completed').reduce((sum, r) => sum + r.refundAmount, 0);
  // Filter refunds
  const filteredRefunds = refunds.filter(refund => {
    const matchesSearch = refund.refundId.toLowerCase().includes(searchQuery.toLowerCase()) || refund.transactionId.toLowerCase().includes(searchQuery.toLowerCase()) || refund.customerName.toLowerCase().includes(searchQuery.toLowerCase()) || refund.customerEmail.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || refund.status === statusFilter;
    const matchesReason = reasonFilter === 'all' || refund.reason === reasonFilter;
    return matchesSearch && matchesStatus && matchesReason;
  });
  const getStatusBadge = (status: RefundStatus) => {
    const styles = {
      requested: 'bg-blue-100 text-blue-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      requested: 'Requested',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      processing: 'Processing',
      completed: 'Completed',
      rejected: 'Rejected',
      failed: 'Failed',
      cancelled: 'Cancelled'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-8743">
        {labels[status]}
      </span>;
  };
  const getReasonLabel = (reason: RefundReason) => {
    const labels = {
      customer_request: 'Customer Request',
      duplicate_charge: 'Duplicate Charge',
      fraudulent: 'Fraudulent',
      product_not_received: 'Product Not Received',
      product_defective: 'Product Defective',
      service_unsatisfactory: 'Service Unsatisfactory',
      billing_error: 'Billing Error',
      other: 'Other'
    };
    return labels[reason];
  };
  return <div className="space-y-6" data-id="element-8744">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-8745">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-8746">
          <div className="flex items-center justify-between" data-id="element-8747">
            <div data-id="element-8748">
              <div className="text-sm font-medium text-gray-500" data-id="element-8749">
                {t('pages.Refunds.total_refunds')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-8750">
                {totalRefunds}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-8751">
              <RefreshCwIcon size={20} className="text-blue-600" data-id="element-8752" />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-8753">
          <div className="flex items-center justify-between" data-id="element-8754">
            <div data-id="element-8755">
              <div className="text-sm font-medium text-gray-500" data-id="element-8756">
                {t('pages.Refunds.pending_refunds')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-8757">
                {pendingRefunds}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center" data-id="element-8758">
              <ClockIcon size={20} className="text-yellow-600" data-id="element-8759" />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-8760">
          <div className="flex items-center justify-between" data-id="element-8761">
            <div data-id="element-8762">
              <div className="text-sm font-medium text-gray-500" data-id="element-8763">
                {t('pages.Refunds.completed_refunds')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-8764">
                {completedRefunds}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-8765">
              <CheckCircleIcon size={20} className="text-green-600" data-id="element-8766" />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-8767">
          <div className="flex items-center justify-between" data-id="element-8768">
            <div data-id="element-8769">
              <div className="text-sm font-medium text-gray-500" data-id="element-8770">
                {t('pages.Refunds.total_refunded')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-8771">
                {t('pages.RefundDetail.usd')}{' '}
                {totalRefundAmount.toLocaleString('en-US')}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-8772">
              <DollarSignIcon size={20} className="text-red-600" data-id="element-8773" />
            </div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200" data-id="element-8774">
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-4" data-id="element-8775">
          <div className="relative flex-grow" data-id="element-8776">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8777">
              <SearchIcon size={16} className="text-gray-400" data-id="element-8778" />
            </div>
            <input type="search" aria-label={t('pages.Refunds.attrs.search_by_refund_id_transaction_id')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={t('pages.Refunds.attrs.search_by_refund_id_transaction_id')} data-id="element-8779" />
          </div>
          <div className="flex gap-2" data-id="element-8780">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as RefundStatus | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-8781">
              <option value="all" data-id="element-8782">{t('pages.Refunds.all_statuses')}</option>
              <option value="requested" data-id="element-8783">{t('pages.Refunds.requested')}</option>
              <option value="pending_approval" data-id="element-8784">
                {t('pages.Refunds.pending_approval')}
              </option>
              <option value="approved" data-id="element-8785">{t('pages.Refunds.approved')}</option>
              <option value="processing" data-id="element-8786">
                {t('pages.Refunds.processing')}
              </option>
              <option value="completed" data-id="element-8787">{t('pages.Refunds.completed')}</option>
              <option value="rejected" data-id="element-8788">{t('pages.Refunds.rejected')}</option>
              <option value="failed" data-id="element-8789">{t('pages.Refunds.failed')}</option>
              <option value="cancelled" data-id="element-8790">{t('pages.Refunds.cancelled')}</option>
            </select>
            <select value={reasonFilter} onChange={e => setReasonFilter(e.target.value as RefundReason | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-8791">
              <option value="all" data-id="element-8792">{t('pages.Refunds.all_reasons')}</option>
              <option value="customer_request" data-id="element-8793">
                {t('pages.Refunds.customer_request')}
              </option>
              <option value="duplicate_charge" data-id="element-8794">
                {t('pages.Refunds.duplicate_charge')}
              </option>
              <option value="fraudulent" data-id="element-8795">
                {t('pages.Refunds.fraudulent')}
              </option>
              <option value="product_not_received" data-id="element-8796">
                {t('pages.Refunds.product_not_received')}
              </option>
              <option value="product_defective" data-id="element-8797">
                {t('pages.Refunds.product_defective')}
              </option>
              <option value="service_unsatisfactory" data-id="element-8798">
                {t('pages.Refunds.service_unsatisfactory')}
              </option>
              <option value="billing_error" data-id="element-8799">
                {t('pages.Refunds.billing_error')}
              </option>
              <option value="other" data-id="element-8800">{t('pages.Refunds.other')}</option>
            </select>
          </div>
        </div>
        {/* Refunds List */}
        <div className="overflow-x-auto" data-id="element-8801">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-8802">
            <thead className="bg-gray-50" data-id="element-8803">
              <tr data-id="element-8804">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8805">
                  {t('components.RefundModal.refund')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8806">
                  {t('pages.Refunds.customer')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8807">
                  {t('pages.Refunds.amount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8808">
                  {t('components.RefundModal.reason')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8809">
                  {t('pages.Refunds.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8810">
                  {t('pages.Refunds.requested')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8811">
                  {t('pages.Refunds.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-8812">
              {filteredRefunds.map(refund => <tr key={refund.id} onClick={() => navigate(`/refund-detail/${refund.id}`)} className="hover:bg-gray-50 cursor-pointer" data-id="element-8813">
                  <td className="px-6 py-4" data-id="element-8814">
                    <div data-id="element-8815">
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-8816">
                        {refund.refundId}
                        {refund.isPartial && <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-medium rounded" data-id="element-8817">
                            {t('pages.Refunds.partial')}
                          </span>}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-8818">
                        {refund.transactionId}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-8819">
                    <div data-id="element-8820">
                      <div className="text-sm font-medium text-gray-900" data-id="element-8821">
                        {refund.customerName}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-8822">
                        {refund.customerEmail}
                      </div>
                      <div className="text-xs text-gray-400 mt-1" data-id="element-8823">
                        {refund.paymentMethod}{' '}
                        {refund.cardLast4 && `•••• ${refund.cardLast4}`}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-8824">
                    <div data-id="element-8825">
                      <div className="text-sm font-medium text-gray-900" data-id="element-8826">
                        {t('pages.RefundDetail.usd')}{' '}
                        {refund.refundAmount.toLocaleString('en-US')}
                      </div>
                      {refund.isPartial && <div className="text-xs text-gray-500" data-id="element-8827">
                          {t('pages.RefundDetail.of_usd')}{' '}
                          {refund.originalAmount.toLocaleString('en-US')}
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-8828">
                    <div className="text-sm text-gray-900" data-id="element-8829">
                      {getReasonLabel(refund.reason)}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-8830">{getStatusBadge(refund.status)}</td>
                  <td className="px-6 py-4" data-id="element-8831">
                    <div className="text-sm text-gray-900" data-id="element-8832">
                      {new Date(refund.requestedAt).toLocaleDateString('en-GB')}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-8833">
                      {t('pages.Refunds.by')} {refund.requestedBy}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right text-sm" data-id="element-8834">
                    <button onClick={e => {
                  e.stopPropagation();
                  navigate(`/refund-detail/${refund.id}`);
                }} className="text-blue-600 hover:text-blue-900 font-medium" data-id="element-8835">
                      {t('pages.Refunds.view_details')}
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {filteredRefunds.length === 0 && <div className="py-12 flex flex-col items-center justify-center text-center" data-id="element-8836">
            <RefreshCwIcon size={48} className="text-gray-400 mb-4" data-id="element-8837" />
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-8838">
              {t('pages.Refunds.no_refunds_found')}
            </h3>
            <p className="text-gray-500 max-w-md" data-id="element-8839">
              {t('pages.Refunds.no_refunds_match_your_current_search')}
            </p>
          </div>}
      </div>
    </div>;
}